import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, ArrowRight, LogIn } from 'lucide-react';
import { useReaderAuth } from '../context/ReaderAuthContext';
import AuthModal from '../components/AuthModal';
import { PILLAR_ICON_URLS } from '../components/PillarIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_INFO = {
  'module-2': { num: 2, title: 'Perspective-Shifting',   icon: PILLAR_ICON_URLS.perspectiveShifting,   route: '/module-2', desc: 'Developing the ability to see situations through multiple cultural lenses.' },
  'module-3': { num: 3, title: 'Adaptive Communication', icon: PILLAR_ICON_URLS.adaptiveCommunication, route: '/module-3', desc: 'Expressing clearly across cultures without losing your voice.' },
  'module-4': { num: 4, title: 'Relational Integration',  icon: PILLAR_ICON_URLS.relationalIntegration,  route: '/module-4', desc: 'Building trust, influence, and cohesion across cultural boundaries.' },
};

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useReaderAuth();

  const sessionId = searchParams.get('session_id');
  const moduleId  = searchParams.get('module');
  const mod       = MODULE_INFO[moduleId];

  const [status,   setStatus]   = useState('idle');   // idle|loading|success|error|needs_auth
  const [errorMsg, setErrorMsg] = useState('');
  const [showAuth, setShowAuth] = useState(false);

  const verify = useCallback(async () => {
    if (!token || !sessionId || !moduleId) return;
    setStatus('loading');
    try {
      const res = await fetch(
        `${API_URL}/api/reader/verify-session?session_id=${encodeURIComponent(sessionId)}&module_id=${encodeURIComponent(moduleId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setStatus('success');
      } else {
        const d = await res.json().catch(() => ({}));
        setErrorMsg(d.detail || 'Could not verify payment. Please contact support.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Connection error. Please try again.');
      setStatus('error');
    }
  }, [token, sessionId, moduleId]);

  useEffect(() => {
    if (!sessionId || !moduleId || !mod) { navigate('/courses', { replace: true }); return; }
    if (!isAuthenticated()) setStatus('needs_auth');
    else verify();
  }, []);

  // Re-verify once user signs in from the needs_auth state
  useEffect(() => {
    if (status === 'needs_auth' && isAuthenticated() && token) verify();
  }, [token]);

  if (!mod) return null;

  return (
    <div
      data-testid="payment-success-page"
      style={{
        background: '#FAF8F3', minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '40px 20px',
      }}
    >
      {showAuth && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
          initialMode="login"
        />
      )}

      <div style={{ maxWidth: 480, width: '100%' }}>
        {/* Module identity header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={mod.icon} alt={`${mod.title} pillar icon`} width={96} height={96}
            style={{ objectFit: 'contain', display: 'block', margin: '0 auto 20px' }} draggable={false} />
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#C99316', margin: '0 0 8px' }}>
            Module 0{mod.num}
          </p>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, fontWeight: 300, color: '#071B3A', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
            {mod.title}
          </h1>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: 'rgba(7,27,58,0.45)', lineHeight: 1.7, margin: 0 }}>
            {mod.desc}
          </p>
        </div>

        {/* Status card */}
        <div style={{ background: '#fff', border: '1px solid rgba(7,27,58,0.07)', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 4px 24px rgba(7,27,58,0.06)' }}>

          {(status === 'idle' || status === 'loading') && (
            <>
              <style>{`@keyframes _tcl_spin{to{transform:rotate(360deg)}}`}</style>
              <Loader2 size={32} color="#C99316" style={{ display: 'block', margin: '0 auto 16px', animation: '_tcl_spin 1s linear infinite' }} />
              <p style={{ fontFamily: 'Georgia, serif', fontSize: 17, color: '#071B3A', margin: '0 0 6px' }}>Activating your module…</p>
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: 'rgba(7,27,58,0.4)', margin: 0 }}>Confirming payment with Stripe</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={36} color="#22c55e" style={{ display: 'block', margin: '0 auto 16px' }} />
              <p style={{ fontFamily: 'Georgia, serif', fontSize: 20, color: '#071B3A', margin: '0 0 8px' }}>Module unlocked</p>
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: 'rgba(7,27,58,0.45)', margin: '0 0 24px', lineHeight: 1.7 }}>
                {mod.title} is now available in your dashboard.
              </p>
              <Link
                to={mod.route}
                data-testid="start-module-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#071B3A', color: '#FAF8F3',
                  padding: '13px 28px', borderRadius: 8,
                  fontFamily: 'Arial, sans-serif', fontSize: 12,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  textDecoration: 'none', fontWeight: 600,
                }}
              >
                Start Module <ArrowRight size={14} />
              </Link>
              <p style={{ marginTop: 14 }}>
                <Link to="/courses" style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: 'rgba(7,27,58,0.35)', textDecoration: 'underline' }}>
                  Return to all modules
                </Link>
              </p>
            </>
          )}

          {status === 'needs_auth' && (
            <>
              <LogIn size={32} color="#C99316" style={{ display: 'block', margin: '0 auto 16px' }} />
              <p style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#071B3A', margin: '0 0 8px' }}>Sign in to activate</p>
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: 'rgba(7,27,58,0.45)', margin: '0 0 24px', lineHeight: 1.7 }}>
                Your payment was successful. Sign in with the email used at checkout to unlock {mod.title}.
              </p>
              <button
                onClick={() => setShowAuth(true)}
                data-testid="signin-to-activate-btn"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                  background: '#071B3A', color: '#FAF8F3', padding: '13px 28px', borderRadius: 8, border: 'none',
                  fontFamily: 'Arial, sans-serif', fontSize: 12, letterSpacing: '0.12em',
                  textTransform: 'uppercase', cursor: 'pointer', fontWeight: 600,
                }}
              >
                <LogIn size={14} /> Sign In to Activate
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle size={32} color="#ef4444" style={{ display: 'block', margin: '0 auto 16px' }} />
              <p style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#071B3A', margin: '0 0 8px' }}>Something went wrong</p>
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: 'rgba(7,27,58,0.45)', margin: '0 0 24px', lineHeight: 1.7 }}>
                {errorMsg}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button data-testid="retry-verify-btn" onClick={verify}
                  style={{ background: '#071B3A', color: '#FAF8F3', border: 'none', padding: '11px 22px', borderRadius: 8, fontSize: 12, cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  Try Again
                </button>
                <Link to="/courses" style={{ border: '1px solid rgba(7,27,58,0.15)', padding: '11px 22px', borderRadius: 8, fontSize: 12, color: '#071B3A', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center' }}>
                  Go Back
                </Link>
              </div>
            </>
          )}

        </div>

        <p style={{ textAlign: 'center', marginTop: 22, fontFamily: 'Arial, sans-serif', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(7,27,58,0.22)' }}>
          Third Culture Leadership
        </p>
      </div>
    </div>
  );
}
